import i18n from "i18next";
import type { PendingInvite } from "@/lib/api";

/**
 * The words on the share drawer's pending rows (Olivia, 10 Oct, §c):
 * "invited 3 Oct", "expired 9 Oct", and the summary under People.
 */

/** The date's language: the interface's (en-GB for English: 3 Oct, not Oct 3). */
function dateLocale(): string {
  const l = i18n.language ?? "en";
  return l.startsWith("fr") ? "fr-FR" : l.startsWith("es") ? "es-ES" : "en-GB";
}

/** "3 Oct", with the year only when it isn't this one. */
export function shortDate(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const year = d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" as const };
  return new Intl.DateTimeFormat(dateLocale(), { day: "numeric", month: "short", ...year }).format(d);
}

/** "invited 3 Oct" (or "sent again 9 Oct" after a resend), "expired 17 Oct". */
export function inviteDateLabel(invite: PendingInvite, now: Date = new Date()): string {
  if (invite.status === "expired") return i18n.t("sharing:dates.expired", { date: shortDate(invite.expiresAt, now) });
  const sameDay = shortDate(invite.sentAt, now) === shortDate(invite.invitedAt, now);
  return sameDay
    ? i18n.t("sharing:dates.invited", { date: shortDate(invite.invitedAt, now) })
    : i18n.t("sharing:dates.sentAgain", { date: shortDate(invite.sentAt, now) });
}

/** "4 people · 1 pending · 1 expired": the owner and those who joined, then the invitations. */
export function peopleSummary(peopleWithAccess: number, invites: PendingInvite[]): string {
  const pending = invites.filter((i) => i.status === "pending").length;
  const expired = invites.length - pending;
  const parts = [i18n.t("sharing:summary.people", { count: peopleWithAccess })];
  if (pending) parts.push(i18n.t("sharing:summary.pending", { count: pending }));
  if (expired) parts.push(i18n.t("sharing:summary.expired", { count: expired }));
  return parts.join(" · ");
}

/** The link in the invitation email, to paste somewhere else. */
export function inviteLink(inviteId: string, origin: string = window.location.origin): string {
  return `${origin}/invites/${inviteId}`;
}
